import {
  GOLD_400,
  PURPLE_350,
  SIENNA_300,
  TEAL_450,
} from '@wandb/weave/common/css/globals.styles';
import {TagColorName} from '@wandb/weave/components/Tag';

import {
  getModelsSeatTypeDisplayName,
  getWeaveRoleTypeDisplayName,
  ModelSeatTypeColumnName,
  ModelsSeatType,
  UserRole,
  UserStatus,
  WeaveRoleType,
} from '../../components/MembersTable/util';
import {
  OrganizationMember,
  User,
  UserAccountType,
} from '../../generated/graphql';

export const getUserRole = (user: Partial<User>): UserRole => {
  if (user.accountType === 'SERVICE') {
    return UserRole.Service;
  }
  if (user.admin) {
    return UserRole.Admin;
  }
  if (user.accountType === 'VIEWER') {
    return UserRole.Viewer;
  }
  return UserRole.Member;
};

export const getUserRoleWithWeaveRole = (user: Partial<User>): UserRole => {
  if (user.accountType === 'SERVICE') {
    return UserRole.Service;
  }
  if (user.accountType === 'VIEWER') {
    return UserRole.Viewer;
  }
  if (user.admin) {
    return UserRole.Admin;
  }
  return UserRole.Member;
};

export const getUserModelsSeats = (
  user: Partial<User>,
  members: OrganizationMember[] | undefined
): ModelsSeatType => {
  const modelSeat = members?.find(mem => mem.id === user.id)?.modelsSeat ?? '';

  if (members && modelSeat.length > 0) {
    return getModelsSeatTypeDisplayName(modelSeat as ModelSeatTypeColumnName);
  }

  if (user.accountType === 'VIEWER') {
    return ModelsSeatType.VIEWER;
  }
  if (user.admin) {
    return ModelsSeatType.FULL;
  }
  if (user.accountType === 'USER') {
    return ModelsSeatType.FULL;
  }

  return ModelsSeatType.NOACCESS;
};

export const getUserWeaveRole = (
  user: Partial<User>,
  members: OrganizationMember[] | undefined
): WeaveRoleType => {
  const weaveRole = members?.find(mem => mem.id === user.id)?.weaveRole ?? '';

  if (members && weaveRole.length > 0) {
    return getWeaveRoleTypeDisplayName(weaveRole);
  }

  if (user.accountType === 'VIEWER') {
    return WeaveRoleType.VIEWER;
  }
  if (user.admin) {
    return WeaveRoleType.INCLUDED;
  }
  if (user.accountType === 'USER') {
    return WeaveRoleType.INCLUDED;
  }

  return WeaveRoleType.NOACCESS;
};

export const getUserStatus = (user: Partial<User>): UserStatus => {
  const isUserOrViewer =
    user.accountType === UserAccountType.User ||
    user.accountType === UserAccountType.Viewer;

  if (!user.username && !user.deletedAt && isUserOrViewer) {
    return UserStatus.Pending;
  }
  if (user.deletedAt) {
    return UserStatus.Deactivated;
  }
  if (!user.deletedAt) {
    return UserStatus.Active;
  }
  return UserStatus.Unknown;
};

/**
 * These special users are automatically added in W&B server
 * instances for onboarding and restoration flows. Hide from
 * customers because it's not useful to them.
 */
const SPECIAL_EMAILS = [
  'local@wandb.com',
  'restore@wandb.com',
  'service@wandb.com',
];

// generic type T to avoid type refinement by the filter
// This makes it clear to Typescript that this function returns the
// same type as the input
export const hideSpecialUsers = <
  T extends Pick<User, 'email' | 'accountType' | 'deletedAt'>
>(
  users: T[]
): T[] =>
  users.filter(user => {
    const isSpecialEmail = SPECIAL_EMAILS.includes(user.email ?? '');

    // No user actions are allowed for service accounts in UAD.
    // This means a deactivated service account cannot be re-enabled
    // or removed, so it's not valuable for customers to see them.
    const isDeactivatedServiceAccount =
      user.accountType === 'SERVICE' && user.deletedAt;

    return !isSpecialEmail && !isDeactivatedServiceAccount;
  });

export function getFormattedLocaleDate(date: Date | string): string {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

type LegendInfo = {
  label: string;
  pillColor: TagColorName;
  tailwindColor: string;
  hexColor: string;
};

// Note: if any of these colors are changed, make sure to do the same thing in the vega spec
export const USER_ROLE_LEGEND: Record<string, LegendInfo> = {
  Member: {
    label: 'Members',
    pillColor: 'teal',
    tailwindColor: 'bg-teal-450',
    hexColor: TEAL_450,
  },
  Admin: {
    label: 'Admins',
    pillColor: 'sienna',
    tailwindColor: 'bg-sienna-300',
    hexColor: SIENNA_300,
  },
  Viewer: {
    label: 'Viewers',
    pillColor: 'gold',
    tailwindColor: 'bg-gold-400',
    hexColor: GOLD_400,
  },
  Service: {
    label: 'Service',
    pillColor: 'purple',
    tailwindColor: 'bg-purple-350',
    hexColor: PURPLE_350,
  },
};
